import React, { useMemo, useState, useEffect, useContext } from 'react';
import '../../css/Dashboard.css';
import QueriesComponent from './QueriesComponent';
import FollowUpsComponent from './FollowUpsComponent';
import QueriesOverTimeComponent from './QueriesOverTimeComponent';
import FiltersBar from '../dashboard/FiltersBar';
import StatsRow from '../dashboard/StatsRow';
import PaymentsRow from '../dashboard/PaymentsRow';
import DepartingPackages from '../dashboard/DepartingPackages';
import ByTypeChart from '../dashboard/ByTypeChart';
import ByAssigneeChart from '../dashboard/ByAssigneeChart';
import BookingStackedChart from '../dashboard/BookingStackedChart';
import AttendanceComponent from '../dashboard/AttendanceComponent';
import QuickCheckInModal from '../Attendance/QuickCheckInModal';
import QuickCheckOutModal from '../Attendance/QuickCheckOutModal';
import { DashboardDataProvider, useDashboardData } from './DashboardDataContext';
import { AuthContext } from '../../context/AuthContext';
import { fetchTodayAttendance } from '../../api/Attendance';
import * as FiIcons from 'react-icons/fi';
import * as MdIcons from 'react-icons/md';

const FiCheckSquare = FiIcons.FiCheckSquare as unknown as React.FC;
const MdCheckCircle = MdIcons.MdCheckCircle as unknown as React.FC;

const SalesDashboard = () => {
  // Filter state (would feed API queries)
  const [month, setMonth] = useState<'' | 'prev' | 'last3' | 'ytd'>('');
  const [assignee, setAssignee] = useState<string>('');
  const [showQuickCheckIn, setShowQuickCheckIn] = useState(false);
  const [showQuickCheckOut, setShowQuickCheckOut] = useState(false);
  const { user } = useContext(AuthContext);
  const [todayAttendance, setTodayAttendance] = useState<any>(null);
  const [checkingAttendance, setCheckingAttendance] = useState(true);

  const filters = useMemo(() => ({ month, assignee }), [month, assignee]);
  const attendanceMarkedToday = Boolean(todayAttendance);
  const attendanceCheckedOutToday = Boolean(todayAttendance?.checkOutTime);

  // Check if attendance is already marked for today
  useEffect(() => {
    const checkTodayAttendance = async () => {
      if (!user?.id) {
        setCheckingAttendance(false);
        return;
      }

      try {
        const userId = typeof user.id === 'string' ? parseInt(user.id) : user.id;
        const todayAttendances = await fetchTodayAttendance(userId);
        const existing = Array.isArray(todayAttendances) && todayAttendances.length > 0 ? todayAttendances[0] : null;
        setTodayAttendance(existing);
      } catch (error) {
        console.error('Failed to check today attendance:', error);
      } finally {
        setCheckingAttendance(false);
      }
    };

    checkTodayAttendance();
  }, [user]);

  const FilterBarWithOptions: React.FC = () => {
    const { data } = useDashboardData();
    const assigneeOptions = useMemo(
      () =>
        (data?.queriesByAssignee || []).map(x => ({
          value: String(x.assigneeId),
          label: x.assigneeName,
        })),
      [data]
    );
    return (
      <FiltersBar
        month={month}
        assignee={assignee}
        onChange={next => {
          if (next.month !== undefined) setMonth(next.month);
          if (next.assignee !== undefined) setAssignee(next.assignee);
        }}
        assigneeOptions={assigneeOptions}
      />
    );
  };

  return (
    <div className="db-container crm-page">
      <DashboardDataProvider filters={filters}>
        <div className="db-header">
          <div className="db-header-left">
            <h2>Dashboard</h2>
            <p className="db-header-subtitle">
              Today’s overview of customers, queries, follow-ups and bookings.
            </p>
          </div>

          <div className="db-header-right">
            <FilterBarWithOptions />
            <div className="db-header-actions">
              <button
                className="btn"
                onClick={() => {
                  if (!attendanceMarkedToday) {
                    setShowQuickCheckIn(true);
                    return;
                  }

                  if (!attendanceCheckedOutToday) {
                    setShowQuickCheckOut(true);
                  }
                }}
                disabled={checkingAttendance || (attendanceMarkedToday && attendanceCheckedOutToday)}
                title={
                  checkingAttendance
                    ? 'Checking attendance status'
                    : !attendanceMarkedToday
                      ? 'Mark your attendance'
                      : attendanceCheckedOutToday
                        ? 'Attendance already checked out for today'
                        : 'Check out'
                }
              >
                {checkingAttendance ? (
                  <>Checking...</>
                ) : !attendanceMarkedToday ? (
                  <>
                    <FiCheckSquare /> Mark attendance
                  </>
                ) : attendanceCheckedOutToday ? (
                  <>
                    <span className="attendance-icon">
                      <MdCheckCircle />
                    </span>
                    Attendance checked out
                  </>
                ) : (
                  <>
                    <FiCheckSquare /> Check-out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="db-row">
          <StatsRow filters={filters} />
        </div>

        <div className="db-row db-grid-3">
          <div className="card">
            <QueriesComponent />
          </div>
          <div className="card">
            <FollowUpsComponent />
          </div>
          <div className="card">
            <AttendanceComponent />
          </div>
        </div>

        <div className="db-row db-grid-2">
          <div className="card">
            <DepartingPackages />
          </div>
          <div className="card">
            <PaymentsRow />
          </div>
        </div>

        <div className="db-row db-grid-2">
          <div className="card">
            <ByTypeChart />
          </div>
          <div className="card">
            <ByAssigneeChart />
          </div>
        </div>

        <div className="db-row db-grid-2">
          <div className="card">
            <QueriesOverTimeComponent />
          </div>
          <div className="card">
            <BookingStackedChart />
          </div>
        </div>
      </DashboardDataProvider>

      {showQuickCheckIn && (
        <QuickCheckInModal
          onClose={() => {
            setShowQuickCheckIn(false);
            if (!user?.id) return;
            const checkTodayAttendance = async () => {
              try {
                const userId =
                  typeof user.id === 'string' ? parseInt(user.id) : user.id;
                const todayAttendances = await fetchTodayAttendance(userId);
                const existing =
                  Array.isArray(todayAttendances) && todayAttendances.length > 0
                    ? todayAttendances[0]
                    : null;
                setTodayAttendance(existing);
              } catch (error) {
                console.error('Failed to check today attendance:', error);
              }
            };
            checkTodayAttendance();
          }}
          onSuccess={() => {
            setShowQuickCheckIn(false);
            if (!user?.id) return;
            const refreshAttendance = async () => {
              try {
                const userId =
                  typeof user.id === 'string' ? parseInt(user.id) : user.id;
                const todayAttendances = await fetchTodayAttendance(userId);
                const existing =
                  Array.isArray(todayAttendances) && todayAttendances.length > 0
                    ? todayAttendances[0]
                    : null;
                setTodayAttendance(existing);
              } catch (error) {
                console.error('Failed to refresh today attendance:', error);
              }
            };
            refreshAttendance();
          }}
        />
      )}

      {showQuickCheckOut && (
        <QuickCheckOutModal
          onClose={() => {
            setShowQuickCheckOut(false);
            if (!user?.id) return;
            const refreshAttendance = async () => {
              try {
                const userId =
                  typeof user.id === 'string' ? parseInt(user.id) : user.id;
                const todayAttendances = await fetchTodayAttendance(userId);
                const existing =
                  Array.isArray(todayAttendances) && todayAttendances.length > 0
                    ? todayAttendances[0]
                    : null;
                setTodayAttendance(existing);
              } catch (error) {
                console.error('Failed to refresh today attendance:', error);
              }
            };
            refreshAttendance();
          }}
          onSuccess={() => {
            setShowQuickCheckOut(false);
            if (!user?.id) return;
            const refreshAttendance = async () => {
              try {
                const userId =
                  typeof user.id === 'string' ? parseInt(user.id) : user.id;
                const todayAttendances = await fetchTodayAttendance(userId);
                const existing =
                  Array.isArray(todayAttendances) && todayAttendances.length > 0
                    ? todayAttendances[0]
                    : null;
                setTodayAttendance(existing);
              } catch (error) {
                console.error('Failed to refresh today attendance:', error);
              }
            };
            refreshAttendance();
          }}
        />
      )}
    </div>
  );
};

export default SalesDashboard;
